// Story 8.12 (ADO-2021, D-6/D-7) — the ONE on-order formatter (ORDER_STATUS_MODEL.md §10: "one
// formatter, one date format"), shared by the chip on the product card and the review row and by
// the product-detail On Order block.
//
// Both dates are DD/MM/YYYY. The order date is an instant and goes through `formatSubmittedDate`,
// the formatter the review row's LAST ORDERED cell uses, so the two dates that sit side by side
// in one row can never disagree. The ETA is a calendar day, re-ordered as text with no Date and no
// zone, so it can never move across midnight.
//
// The cart side panel (Figma Cart Drawer Row 5008:41020) shows the same chip text as the product
// card, by user decision (2026-10-06), so the order date is always there even without an ETA.

import type { OnOrderEntry, OnOrderProduct } from '../api/on-order.client';
import { formatSubmittedDate } from '../shared/datetime';

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `2026-02-18` → `18/02/2026`; anything else is returned unchanged. */
export function formatEtaDay(isoDay: string): string {
  const match = CALENDAR_DAY.exec(isoDay);
  return match === null ? isoDay : `${match[3]}/${match[2]}/${match[1]}`;
}

/** The order instant as the viewer's DD/MM/YYYY day (the LAST ORDERED cell's formatter). */
export function formatOrderedOn(orderedAtIso: string): string {
  return formatSubmittedDate(orderedAtIso);
}

/**
 * Which Cart an order came from, when it is not the one being ordered at (AC2): `{depot name}
 * cart`, else `{depot code} cart`. `null` for an order from the active depot.
 */
export function cartLabelFor(order: OnOrderEntry, activeDepotId: string | null): string | null {
  if (order.depotId !== undefined && order.depotId === activeDepotId) return null;
  return `${order.depotName ?? order.depotCode} cart`;
}

/**
 * The chip's text for a product on order: the newest order's date, then its dispatched quantity
 * and ETA when known. The chip never names the order's Cart (the product-detail block does).
 * Older orders are not counted here (that block lists every order). `''` when there is no order.
 */
export function onOrderChipText(product: OnOrderProduct): string {
  const [newest] = product.orders;
  if (newest === undefined) return '';
  const parts = [`On order ${formatOrderedOn(newest.orderedAt)}`];
  if (newest.dispatchedQuantity !== undefined)
    parts.push(`${newest.dispatchedQuantity} dispatched`);
  if (newest.estimatedDeliveryDate !== undefined) {
    parts.push(`ETA ${formatEtaDay(newest.estimatedDeliveryDate)}`);
  }
  return parts.join(' · ');
}

/**
 * One order's line in the product-detail On Order block: its date, ETA ("Due") when known, the
 * quantity, the dispatched quantity when known, and its Cart when not the active one.
 */
export function onOrderDetailText(order: OnOrderEntry, activeDepotId: string | null): string {
  const parts = [`Date: ${formatOrderedOn(order.orderedAt)}`];
  if (order.estimatedDeliveryDate !== undefined) {
    parts.push(`Due: ${formatEtaDay(order.estimatedDeliveryDate)}`);
  }
  parts.push(`Amount: ${order.quantity}`);
  if (order.dispatchedQuantity !== undefined) parts.push(`${order.dispatchedQuantity} dispatched`);
  const cart = cartLabelFor(order, activeDepotId);
  if (cart !== null) parts.push(cart);
  return parts.join(' · ');
}
